import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

/**
 * Danger-zone card on the Account page.
 *
 * Users can permanently delete their own account. They must:
 *   1. Provide a reason (min 8 chars).
 *   2. Type `DELETE MY ACCOUNT` to enable the destroy button.
 *
 * Once submitted, their email is added to `banned_emails` on the backend
 * and any future magic-link/verify for that address is refused. Copy is
 * bilingual (English + Arabic) so Arabic-speaking readers aren't guessing.
 */
const DeleteAccountCard = ({ onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const canSubmit =
    reason.trim().length >= 8 && confirm.trim().toUpperCase() === CONFIRM_PHRASE && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/me/delete-account`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: reason.trim(),
          confirm_phrase: confirm.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed.');
      setDone(true);
      // Give the reader a beat to see the confirmation, then bounce.
      setTimeout(() => {
        if (typeof onDeleted === 'function') onDeleted();
        window.location.href = '/';
      }, 2500);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="account-deleted-notice"
        className="border-2 border-red-700 bg-red-950/40 rounded-lg p-5 mt-6"
      >
        <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-2">
          Account deleted
        </p>
        <p className="text-white font-black text-lg">
          Your account is gone. This email is now permanently banned from Outlaw Media.
        </p>
        <p className="text-zinc-400 text-sm mt-1" dir="rtl" lang="ar">
          تم حذف حسابك. لن تتمكن من التسجيل مرة أخرى بهذا البريد الإلكتروني على الإطلاق.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="danger-zone-card"
      className="border-2 border-red-800/70 bg-red-950/20 rounded-lg p-5 mt-8"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-red-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.3em] mb-1">
            Danger Zone / منطقة الخطر
          </p>
          <p className="text-white font-black text-xl leading-tight">
            Delete My Account - Permanent
          </p>
          <p className="text-white font-black text-xl leading-tight mt-1" dir="rtl" lang="ar">
            حذف حسابي - نهائي
          </p>

          <div className="mt-4 space-y-3 text-sm">
            <p className="text-white font-bold leading-relaxed">
              <strong>Warning:</strong> Deleting your account is permanent. You will be
              banned from ever creating another account with this email. There is no undo.
              Any subscription must be cancelled separately with the billing provider.
            </p>
            <p
              className="text-white font-bold leading-relaxed"
              dir="rtl"
              lang="ar"
            >
              <strong>تحذير:</strong> حذف حسابك دائم ولا يمكن التراجع عنه. سيتم حظرك من
              إنشاء أي حساب جديد بهذا البريد الإلكتروني إلى الأبد. يجب إلغاء أي اشتراك
              مدفوع بشكل منفصل مع مزود الفوترة.
            </p>
          </div>

          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="danger-zone-open"
              className="mt-5 inline-flex items-center bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.3em] px-5 py-3 rounded"
            >
              Delete my account / احذف حسابي
            </button>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="danger-zone-form">
              <div>
                <label
                  htmlFor="delete-reason"
                  className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1"
                >
                  Reason (required) / السبب (مطلوب)
                </label>
                <textarea
                  id="delete-reason"
                  data-testid="delete-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  minLength={8}
                  maxLength={500}
                  placeholder="Tell us why you're leaving. Minimum 8 characters."
                  className="w-full bg-black border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none"
                  required
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  {reason.trim().length}/500 characters
                </p>
              </div>

              <div>
                <label
                  htmlFor="delete-confirm"
                  className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1"
                >
                  Type <span className="text-red-400 font-black">{CONFIRM_PHRASE}</span> to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  data-testid="delete-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  className="w-full bg-black border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none font-mono"
                  autoComplete="off"
                />
              </div>

              {err && (
                <p className="text-red-400 text-xs" data-testid="delete-error">
                  {err}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="delete-account-submit"
                  className="bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-xs uppercase tracking-[0.3em] px-5 py-3 rounded"
                >
                  {busy ? 'Deleting…' : 'Permanently delete my account'}
                </button>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setReason(''); setConfirm(''); setErr(''); }}
                  disabled={busy}
                  data-testid="delete-account-cancel"
                  className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-black text-xs uppercase tracking-[0.3em] px-5 py-3 rounded"
                >
                  Cancel / إلغاء
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountCard;
